import { Truck, Home, Briefcase, type LucideIcon } from "lucide-react";
import type { Department } from "@/features/auth/types";

/** Application type the job routes to on the Apply form. */
export type JobApplyType = "driver" | "carrier" | "office";

/** Human label for an application type (used on cards, badges, the admin list). */
export const APPLY_TYPE_LABEL: Record<JobApplyType, string> = {
  driver: "Company driver",
  carrier: "Owner-operator",
  office: "Office / Corporate",
};

/**
 * A job posting. This is PLAIN DATA (no React components) so the exact same
 * shape can be stored in the `jobs` Supabase table and edited by staff in the
 * browser. The card icon is derived from `applyType` (see jobIcon).
 */
export interface Job {
  slug: string;
  title: string;
  /** Who this is for - drives the Apply form type + the card accent. */
  applyType: JobApplyType;
  /** Short employment descriptor, e.g. "Full-time - OTR". */
  employment: string;
  location: string;
  /** Home-time descriptor, e.g. "Weekly home time". */
  homeTime: string;
  /** Pay range copy (PLACEHOLDER - confirm real numbers with BB). */
  pay: string;
  summary: string;
  requirements: string[];
  /** Optional extra perks specific to this role. */
  highlights: string[];
  /**
   * Department that reviews applicants for this posting. Drives Applications
   * inbox scoping: only that department's manager (and admins) see its
   * applications. Driver/owner-operator postings route to Safety regardless.
   */
  department?: Department | null;
  /** Only published jobs show on the public careers page. */
  published: boolean;
  /** Lower numbers sort first. */
  order: number;
}

/** Card icon for a job, derived from who it targets (DB stores no component). */
export function jobIcon(applyType: JobApplyType): LucideIcon {
  if (applyType === "carrier") return Home;
  if (applyType === "office") return Briefcase;
  return Truck;
}

/**
 * Seed postings (PLACEHOLDER copy/pay - confirm with BB). These are the
 * defaults shown until staff edit them in the portal. When Supabase is
 * connected, the `jobs` table is the source of truth and these become the
 * initial seed only.
 */
export const seedJobs: Job[] = [
  {
    slug: "otr-tanker-driver",
    title: "OTR Tanker Driver",
    applyType: "driver",
    employment: "Full-time - OTR",
    location: "Aurora, IL + surrounding lanes",
    homeTime: "Home most weekends",
    pay: "$75,000 - $90,000 / year",
    summary:
      "Run liquid bulk loads across our 48-state network in late-model equipment, with steady miles and a dispatch team that has your back.",
    requirements: [
      "Valid Class A CDL with tanker (N) endorsement",
      "2+ years verifiable OTR driving experience",
      "Clean MVR and DOT-compliant record",
      "Comfortable with liquid surge and tanker handling",
    ],
    highlights: [
      "Late-model, well-maintained tractors",
      "Steady miles year-round",
      "Tanker-focused fleet with occasional dry van/reefer opportunities",
      "Paid orientation and safety bonuses",
    ],
    department: "safety",
    published: true,
    order: 1,
  },
  {
    slug: "regional-tanker-driver",
    title: "Regional Tanker Driver",
    applyType: "driver",
    employment: "Full-time - Regional",
    location: "Midwest lanes",
    homeTime: "Home weekly",
    pay: "$70,000 - $85,000 / year",
    summary:
      "Stay closer to home running regional Midwest lanes, mostly tanker freight, with occasional dry van or reefer work when customer lanes call for it.",
    requirements: [
      "Valid Class A CDL with tanker (N) endorsement",
      "1+ year verifiable driving experience",
      "Clean MVR and DOT-compliant record",
      "Hazmat endorsement a plus (we help you get it)",
    ],
    highlights: [
      "Predictable weekly home time",
      "Consistent regional freight, primarily tanker",
      "Dedicated dispatch contact",
    ],
    department: "safety",
    published: true,
    order: 2,
  },
  {
    slug: "hazmat-tanker-driver",
    title: "HazMat Tanker Driver",
    applyType: "driver",
    employment: "Full-time - OTR / Regional",
    location: "Nationwide",
    homeTime: "Flexible home time",
    pay: "$80,000 - $100,000 / year",
    summary:
      "Put your hazmat endorsement to work hauling placarded liquid bulk freight at the top of our pay scale.",
    requirements: [
      "Valid Class A CDL with tanker (N) and hazmat (H) endorsements",
      "Current TSA background clearance",
      "2+ years tanker experience preferred",
      "Strong safety and compliance mindset",
    ],
    highlights: [
      "Top-of-scale hazmat pay",
      "Premium, specialized freight",
      "Ongoing safety training provided",
    ],
    department: "safety",
    published: true,
    order: 3,
  },
  {
    slug: "owner-operator",
    title: "Owner-Operator",
    applyType: "carrier",
    employment: "Independent contractor",
    location: "Nationwide",
    homeTime: "You set your schedule",
    pay: "Competitive percentage + fuel program",
    summary:
      "Bring your truck and we'll keep it loaded - steady tanker freight from one dispatch team, fuel and insurance programs, and none of the load-hunting.",
    requirements: [
      "Own or lease your tractor (tanker-capable)",
      "Active MC/DOT authority or willing to run under ours",
      "Valid Class A CDL with tanker (N) endorsement",
      "Clean safety record and current insurance",
    ],
    highlights: [
      "Consistent freight - no chasing loads",
      "Fuel discounts and insurance programs",
      "Fast, paperwork-light onboarding",
    ],
    department: "safety",
    published: true,
    order: 4,
  },
  {
    slug: "safety-department-assistant",
    title: "Safety Department Assistant",
    applyType: "office",
    employment: "Full-time",
    location: "Aurora, IL (in office)",
    homeTime: "",
    pay: "",
    summary:
      "Support our safety team with driver compliance, recordkeeping, and day-to-day coordination that keeps our fleet running safe and DOT-compliant.",
    requirements: [
      "Strong organization and attention to detail",
      "Comfortable with spreadsheets and basic office software",
      "Clear written and verbal communication",
      "Transportation or compliance experience a plus",
    ],
    highlights: [
      "Mon-Fri office schedule",
      "Learn DOT safety and compliance from the ground up",
      "Friendly, no-nonsense team",
    ],
    department: "safety",
    published: true,
    order: 5,
  },
];
